"use client";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction } from "next-safe-action/hooks";
import type { ProfileRecord } from "@henlo/core-supabase";
import {
  setUsernameAction,
  updateProfileAction,
  upsertProfileSettingsAction,
} from "@/actions/profile";

export const HENLO_APP_ID = "henlo";

async function fetchProfile(dynamicUserId?: string | null, address?: string | null) {
  if (!dynamicUserId && !address) return null;
  const params = new URLSearchParams();
  if (dynamicUserId) params.set("dynamicUserId", dynamicUserId);
  if (address) params.set("address", address);
  const response = await fetch(`/api/profile/lookup?${params.toString()}`, {
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Failed to load profile (${response.status})`);
  }
  const json = (await response.json()) as { profile: ProfileRecord | null };
  return json.profile;
}

export function useCurrentProfile() {
  const { user, primaryWallet } = useDynamicContext();
  const dynamicUserId = user?.userId ?? null;
  const address = primaryWallet?.address?.toLowerCase() ?? null;

  const { data: profile } = useReactQuery({
    queryKey: ["current-profile", dynamicUserId, address],
    queryFn: () => fetchProfile(dynamicUserId ?? undefined, address ?? undefined),
    enabled: Boolean(dynamicUserId || address),
    staleTime: 30_000,
  });

  return { profile: profile ?? null };
}

async function fetchProfileSettings(profileId: string, app: string) {
  const params = new URLSearchParams({ profileId, app });
  const response = await fetch(`/api/profile/settings?${params.toString()}`, {
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Failed to load profile settings (${response.status})`);
  }
  const json = (await response.json()) as { settings: Record<string, unknown> | null };
  return json.settings;
}

export function useProfileSettings(app: string = HENLO_APP_ID) {
  const { profile } = useCurrentProfile();
  const { data, isLoading } = useReactQuery({
    queryKey: ["profile-settings", profile?.id, app],
    queryFn: () => fetchProfileSettings(profile!.id, app),
    enabled: Boolean(profile?.id),
    staleTime: 30_000,
  });

  return { settings: (data as any) ?? null, profile, isLoading };
}

export function useUpsertProfileSettings(app: string = HENLO_APP_ID) {
  const { profile } = useCurrentProfile();
  const { execute } = useAction(upsertProfileSettingsAction, {
    onError(error) {
      console.error("Failed to update profile settings", error);
    },
  });

  const update = async (settings: Record<string, unknown>) => {
    await execute({ app, settings });
  };

  return { update, profile };
}

export function useAppAvatar() {
  const { settings } = useProfileSettings(HENLO_APP_ID);
  return (settings as any)?.avatarUrl as string | undefined;
}

export function useSetAppAvatar() {
  const { update } = useUpsertProfileSettings(HENLO_APP_ID);
  return async (avatarUrl: string) => update({ avatarUrl });
}

export function useAppAvatarParts() {
  const { settings } = useProfileSettings(HENLO_APP_ID);
  const character = (settings as any)?.avatarCharacter as string | undefined;
  const background = (settings as any)?.avatarBackground as string | undefined;
  return { character, background };
}

export function useSetAppAvatarParts() {
  const { update } = useUpsertProfileSettings(HENLO_APP_ID);
  return async (character: string, background: string) =>
    update({ avatarCharacter: character, avatarBackground: background });
}

export function useUpdateProfile() {
  const { execute } = useAction(updateProfileAction, {
    onError(error) {
      console.error("Failed to update profile", error);
    },
  });
  return execute;
}
